<script setup lang="ts">
import { useSchoolStudents } from '@/composables/student/useSchoolStudents';
import { Components } from '@/api/generated/api';
import type { TEntityAppearance } from '@/core/entity-appearance/entityAppearance.types';
import { appearanceFromDto } from '@/core/entity-appearance/appearanceFromDto';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSchoolSubjectGroup } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import { useBumpUpClass } from '@/composables/useBumpUpClass';
import { IConfirmDialogProps, useConfirmDialog } from '@/composables/useConfirmDialog';
import { useDirtySnapshot } from '@/composables/useDirtySnapshot';
import { useMobile } from '@/composables/useMobile';
import { useRouterHelper } from '@/composables/useRouterHelper';
import { ICreateOrUpdateSchoolClass, useSchoolClasses } from '@/composables/useSchoolClasses';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { useToast } from '@/composables/useToast';
import { useWhoAmI } from '@/composables/useWhoAmI';
import ArchiveClassDialog from '@/core/dialogs/archive-class/ArchiveClassDialog.vue';
import DialogBase from '@/core/dialogs/base-dialog/DialogBase.vue';
import BumpUpClassSelectClassStep from '@/core/dialogs/create-class/BumpUpClassSelectClassStep.vue';
import BumpUpClassSelectModeStep from '@/core/dialogs/create-class/BumpUpClassSelectModeStep.vue';
import ClassFormFooter, { IClassFormFooterConfig } from '@/core/dialogs/create-class/ClassFormFooter.vue';
import CreateClassFinalize from '@/core/dialogs/create-class/CreateClassFinalize.vue';
import CreateClassGeneralDataForm from '@/core/dialogs/create-class/CreateClassGeneralDataForm.vue';
import CreateClassSchoolSubjectsForm from '@/core/dialogs/create-class/CreateClassSchoolSubjectsForm.vue';
import CreateClassStudentsForm, {
  IStudentFormSelection,
} from '@/core/dialogs/create-class/CreateClassStudentsForm.vue';
import CreateClassTeamForm from '@/core/dialogs/create-class/CreateClassTeamForm.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import { IOnboardingStudent, IUserWithPermissions } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { provideViewedSchoolClass } from '@/schoolClasses/useSchoolClassType';
import { useClassCreationStore } from '@/stores/classCreationStore';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { getCurrentRole } from '@/utils/role.utils';
import { getSchoolYearName } from '@/utils/schoolYear.utils';
import { SchoolClassType, UserRoleType } from '@/api/generated/api.enum';
import { ECreateClassMode } from 'common/enum/misc.enum';
import { getClassName } from 'common/utils/format.utils';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, shallowRef, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import CreateClassNoSchoolSubjectsStep from './CreateClassNoSchoolSubjectsStep.vue';

const emit = defineEmits<{
  (e: 'created', payload: { schoolClass: Components.Schemas.SchoolClassDto; mode: ECreateClassMode }): void;
  (e: 'closed'): void;
}>();

const { createSchoolClassSetup, refresh } = useSchoolClasses();
const { getAvailableSchoolSubjectsForSchoolLevel } = useSchoolSubject();
const { allSchoolSubjectGroupTemplates } = useSchoolSubjectGroup();
const { customSuccessToast, catchError } = useToast();
const isOpen = ref(false);
const { allStudentsAdmin, refresh: refreshSchoolStudents } = useSchoolStudents(isOpen);
const { setCurrentClass } = useCurrentClass();
const { navigateToAllStudents } = useRouterHelper();
const { t } = useI18n();
const { getBumpUpClassConfig } = useBumpUpClass(allStudentsAdmin);
const { confirm } = useConfirmDialog();
const { allSchoolYearsTenant, currentSchoolYear, isSchoolYearInFuture } = useSchoolYears();
const { whoAmI, currentTenant } = useWhoAmI();
const { push } = useRouter();
const classCreationStore = useClassCreationStore();
const { navigateAfterCreation } = storeToRefs(classCreationStore);
const { isMobile } = useMobile();

enum ECreateClassStep {
  SELECT_MODE = 'SELECT_MODE',
  SELECT_CLASS = 'SELECT_CLASS',
  GENERAL_DATA = 'GENERAL_DATA',
  SCHOOL_SUBJECTS = 'SCHOOL_SUBJECTS',
  NO_SCHOOL_SUBJECTS = 'NO_SCHOOL_SUBJECTS',
  TEAM = 'TEAM',
  STUDENTS = 'STUDENTS',
  FINALIZE = 'FINALIZE',
}

const dialogRef = useTemplateRef('dialogRef');
const selectModeStepRef = useTemplateRef('selectModeStepRef');
const selectClassStepRef = useTemplateRef('selectClassStepRef');
const generalDataFormRef = useTemplateRef('generalDataFormRef');
const schoolSubjectsFormRef = useTemplateRef('schoolSubjectsFormRef');
const teamFormRef = useTemplateRef('teamFormRef');
const studentsFormRef = useTemplateRef('studentsFormRef');
const archiveClassDialogRef = useTemplateRef('archiveClassDialogRef');

const availableSchoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const existingStudents = ref<Components.Schemas.StudentDto[]>([]);
const generalData = ref<Components.Schemas.CreateSchoolClassDto | null>(null);
const appearance = shallowRef<TEntityAppearance>(appearanceFromDto());
const newStudents = ref<IOnboardingStudent[]>([]);
const schoolClassToBumpUp = ref<Components.Schemas.SchoolClassDto | null>(null);
const schoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const team = ref<IUserWithPermissions[]>([]);
const studentsSelectionCount = ref(0);

const currentStep = ref<ECreateClassStep>(ECreateClassStep.GENERAL_DATA);
const formValid = ref(true);
const isLoading = ref<boolean>(false);
const savedSchoolClass = ref<Components.Schemas.SchoolClassDto>();
const mode = ref<ECreateClassMode>(ECreateClassMode.NEW_CLASS);
const navigateAfterCreationOverride = ref<boolean>();
const didCreate = ref(false);
const isBumpUpFlow = computed<boolean>(
  () => mode.value === ECreateClassMode.BUMP_UP_CLASS || mode.value === ECreateClassMode.SELECT_CLASS,
);
// a bump-up rebuilds the container it started from; everything else here builds a class
const bumpedSchoolClassType = computed<Components.Schemas.SchoolClassType>(
  () => schoolClassToBumpUp.value?.type ?? SchoolClassType.class,
);
provideViewedSchoolClass(() => ({ type: bumpedSchoolClassType.value }));
const { tContainer } = useContainerTranslations(() => ({ type: bumpedSchoolClassType.value }));
const isBumpingGroup = computed<boolean>(() => bumpedSchoolClassType.value === SchoolClassType.group);
// a group owns exactly one subject and it carries over on its own — there is nothing to pick, unless the
// next level offers no subject of that name and the user has to choose one
const skipSchoolSubjectsStep = computed<boolean>(() => isBumpingGroup.value && schoolSubjects.value.length === 1);

const { captureSnapshot, isDirty } = useDirtySnapshot(() => appearance.value, currentStep);

const reset = (): void => {
  isOpen.value = false;
  savedSchoolClass.value = undefined;
  availableSchoolSubjects.value = [];
  currentStep.value = ECreateClassStep.GENERAL_DATA;
  existingStudents.value = [];
  generalData.value = null;
  appearance.value = appearanceFromDto();
  newStudents.value = [];
  schoolClassToBumpUp.value = null;
  schoolSubjects.value = [];
  team.value = [];
  studentsSelectionCount.value = 0;

  formValid.value = true;
  isLoading.value = false;
  mode.value = ECreateClassMode.NEW_CLASS;
  navigateAfterCreationOverride.value = undefined;
};

const discardChangesConfirmConfig = computed<IConfirmDialogProps>(() => ({
  title: t('general.cancelEntry'),
  description: t('general.cancelEntryDescription'),
  declineButtonLabel: t('general.continueEditing'),
  confirmButtonLabel: t('general.cancelEntry'),
  showConfirmOn: isDirty,
}));

const handleHide = (): void => {
  reset();
  if (!didCreate.value) {
    emit('closed');
  }
  didCreate.value = false;
};

const handleBumpUpClassWithConfirm = async (schoolClass: Components.Schemas.SchoolClassDto): Promise<boolean> => {
  // the confirm and the archive dialog it can open name this container, so it is known before they run
  schoolClassToBumpUp.value = schoolClass;
  const confirmed = await confirmBumpUp(schoolClass);
  if (!confirmed) {
    schoolClassToBumpUp.value = null;
    return false;
  }

  try {
    isLoading.value = true;
    const bumpUpClassConfig = await getBumpUpClassConfig(schoolClass);
    existingStudents.value = bumpUpClassConfig.existingStudents;
    generalData.value = bumpUpClassConfig.generalData;
    appearance.value = appearanceFromDto(schoolClass.appearance);
    schoolSubjects.value = bumpUpClassConfig.schoolSubjects;
    team.value = bumpUpClassConfig.team;
  } finally {
    isLoading.value = false;
  }
  return true;
};

const footerConfig = computed<IClassFormFooterConfig>(() => {
  switch (currentStep.value) {
    case ECreateClassStep.SELECT_MODE:
      return {
        nextBtnLabel: t('general.continue'),
      };
    case ECreateClassStep.SELECT_CLASS:
      return {
        nextBtnLabel: t('general.continue'),
      };
    case ECreateClassStep.GENERAL_DATA:
      return {
        nextBtnLabel: t('general.continue'),
        previousBtnLabel: mode.value === ECreateClassMode.SELECT_CLASS ? t('general.back') : undefined,
      };
    case ECreateClassStep.SCHOOL_SUBJECTS:
      return {
        nextBtnLabel: t('general.continue'),
        previousBtnLabel: t('general.back'),
        helpText: t('onboarding.schoolSubject.helpText'),
      };
    case ECreateClassStep.NO_SCHOOL_SUBJECTS:
      return {
        previousBtnLabel: t('general.back'),
      };
    case ECreateClassStep.TEAM:
      return {
        nextBtnLabel: t('general.continue'),
        previousBtnLabel: t('general.back'),
      };
    case ECreateClassStep.STUDENTS:
      return {
        helpText: t('onboarding.students.helpText'),
        nextBtnLabel: mode.value === ECreateClassMode.MINIMAL ? t('classes.createAction') : t('general.continue'),
        previousBtnLabel: t('general.back'),
      };
    case ECreateClassStep.FINALIZE:
    default:
      return {
        previousBtnLabel: t('general.back'),
        nextBtnLabel: mode.value === ECreateClassMode.NEW_CLASS ? t('classSetup.title') : tContainer('bumpUp'),
        buttonSpacing: isMobile.value ? 'between' : 'end',
      };
  }
});

const showNextStep = (): void => {
  switch (currentStep.value) {
    case ECreateClassStep.SELECT_MODE:
      if (mode.value === ECreateClassMode.SELECT_CLASS) {
        currentStep.value = ECreateClassStep.SELECT_CLASS;
      } else {
        currentStep.value = ECreateClassStep.GENERAL_DATA;
      }
      break;
    case ECreateClassStep.SELECT_CLASS:
      currentStep.value = ECreateClassStep.GENERAL_DATA;
      break;
    case ECreateClassStep.GENERAL_DATA:
      if (isBumpUpFlow.value) {
        currentStep.value = skipSchoolSubjectsStep.value ? ECreateClassStep.TEAM : ECreateClassStep.SCHOOL_SUBJECTS;
        break;
      }
      if (!availableSchoolSubjects.value.length) {
        currentStep.value = ECreateClassStep.NO_SCHOOL_SUBJECTS;
        break;
      }
      if (mode.value === ECreateClassMode.MINIMAL) {
        currentStep.value = ECreateClassStep.STUDENTS;
      } else {
        currentStep.value = ECreateClassStep.TEAM;
      }
      break;
    case ECreateClassStep.SCHOOL_SUBJECTS:
      currentStep.value = ECreateClassStep.TEAM;
      break;
    case ECreateClassStep.TEAM:
      currentStep.value = ECreateClassStep.STUDENTS;
      break;
    case ECreateClassStep.STUDENTS:
      if (mode.value === ECreateClassMode.MINIMAL) {
        currentStep.value = ECreateClassStep.STUDENTS;
      } else {
        currentStep.value = ECreateClassStep.FINALIZE;
      }
      break;
  }
};

const showPreviousStep = (): void => {
  if (isLoading.value) return;
  switch (currentStep.value) {
    case ECreateClassStep.SELECT_CLASS:
      currentStep.value = ECreateClassStep.SELECT_MODE;
      break;
    case ECreateClassStep.GENERAL_DATA:
      currentStep.value = ECreateClassStep.SELECT_CLASS;
      break;
    case ECreateClassStep.SCHOOL_SUBJECTS:
      currentStep.value = ECreateClassStep.GENERAL_DATA;
      break;
    case ECreateClassStep.NO_SCHOOL_SUBJECTS:
      currentStep.value = ECreateClassStep.GENERAL_DATA;
      break;
    case ECreateClassStep.TEAM:
      currentStep.value =
        isBumpUpFlow.value && !skipSchoolSubjectsStep.value
          ? ECreateClassStep.SCHOOL_SUBJECTS
          : ECreateClassStep.GENERAL_DATA;
      break;
    case ECreateClassStep.STUDENTS:
      currentStep.value =
        mode.value === ECreateClassMode.MINIMAL ? ECreateClassStep.GENERAL_DATA : ECreateClassStep.TEAM;
      break;
    case ECreateClassStep.FINALIZE:
      currentStep.value = ECreateClassStep.STUDENTS;
      break;
  }
};

const handleSelectModeStepDone = (newMode: ECreateClassMode): void => {
  mode.value = newMode;
  showNextStep();
};

const handleSelectClassStepDone = async (schoolClass: Components.Schemas.SchoolClassDto | null): Promise<void> => {
  assertNotNullOrUndefined(schoolClass);

  const confirmed = await handleBumpUpClassWithConfirm(schoolClass);
  if (!confirmed) return;

  showNextStep();
};

const confirmFutureSchoolYearCreation = async (schoolYearId: number): Promise<boolean> => {
  const activeSchoolYear = currentSchoolYear.value;
  if (isBumpUpFlow.value || !activeSchoolYear) return true;

  const selectedSchoolYear = allSchoolYearsTenant.value.find((schoolYear) => schoolYear.id === schoolYearId);
  if (!selectedSchoolYear || !isSchoolYearInFuture(selectedSchoolYear)) return true;

  const shouldUseCurrentSchoolYear = await confirm({
    title: t('classes.futureSchoolYearConfirm.title'),
    description: tContainer('futureSchoolYearDescription', {
      selectedSchoolYearName: getSchoolYearName(selectedSchoolYear, t),
      currentSchoolYearName: getSchoolYearName(activeSchoolYear, t),
    }),
    confirmButtonLabel: t('classes.futureSchoolYearConfirm.declineButtonLabel'),
    declineButtonLabel: t('classes.futureSchoolYearConfirm.confirmButtonLabel'),
    declineOnClose: false,
    hideCloseButton: true,
    theme: 'primary',
    size: 'lg',
  });

  if (shouldUseCurrentSchoolYear) {
    generalDataFormRef.value?.setSchoolYearId(activeSchoolYear.id);
    return false;
  }

  return true;
};

const handleGeneralDataStepDone = async (data: Components.Schemas.CreateSchoolClassDto): Promise<void> => {
  const confirmed = await confirmFutureSchoolYearCreation(data.schoolYearId);
  if (!confirmed) return;

  generalData.value = data;
  try {
    isLoading.value = true;
    availableSchoolSubjects.value = await getAvailableSchoolSubjectsForSchoolLevel(data.schoolLevel);
    if (!isBumpUpFlow.value) {
      // For new class creation we always import all available templates!
      schoolSubjects.value = [...availableSchoolSubjects.value];
    }
  } finally {
    isLoading.value = false;
  }

  if (!isBumpUpFlow.value && !availableSchoolSubjects.value.length) {
    currentStep.value = ECreateClassStep.NO_SCHOOL_SUBJECTS;
    return;
  }

  if (mode.value === ECreateClassMode.MINIMAL) {
    const currentRole = getCurrentRole(whoAmI.value);
    const roleType = currentRole?.roleType ?? UserRoleType.user;
    if (whoAmI.value) {
      team.value = [
        {
          user: {
            id: whoAmI.value.id,
            createdAt: whoAmI.value.createdAt,
            updatedAt: whoAmI.value.updatedAt,
            email: whoAmI.value.email,
            firstName: whoAmI.value.firstName,
            lastName: whoAmI.value.lastName,
            avatar: whoAmI.value.avatar,
            avatarPictureId: whoAmI.value.avatarPictureId,
            isSetupCompleted: whoAmI.value.isSetupCompleted,
            isApproved: whoAmI.value.isApproved,
            countryId: whoAmI.value.countryId,
            stateId: whoAmI.value.stateId,
            demoClassWizardSeen: whoAmI.value.demoClassWizardSeen,
            ratingDialogSeenAt: whoAmI.value.ratingDialogSeenAt,
            source: whoAmI.value.source,
            sourceOther: whoAmI.value.sourceOther,
            weeklyReportActive: whoAmI.value.weeklyReportActive,
            deactivatedAt: whoAmI.value.deactivatedAt,
            language: whoAmI.value.language,
          },
          userRoleType: roleType,
          permissions: {
            canManageAccessRights: true,
            canManageStudents: true,
            canManagePerformanceAssessments: true,
            canManageCompetences: true,
          },
          schoolSubjects: [...schoolSubjects.value],
          schoolClass: null,
        },
      ];
    }
    currentStep.value = ECreateClassStep.STUDENTS;
    return;
  }
  showNextStep();
};

const handleSchoolSubjectsStepDone = (data: Components.Schemas.SchoolSubjectDto[]): void => {
  schoolSubjects.value = data;
  showNextStep();
};

const goToSchoolCompetences = async (): Promise<void> => {
  assertNotNullOrUndefined(currentTenant.value?.id);
  await push({
    name: '//school/[schoolId]/settings/schoolSubjectTemplates/',
    params: { schoolId: currentTenant.value.id },
  });
};

const handleTeamStepDone = (data: IUserWithPermissions[]): void => {
  team.value = data;
  showNextStep();
};

const handleStudentsStepDone = (data: IStudentFormSelection): void => {
  newStudents.value = data.students;
  existingStudents.value = data.selectedStudents ?? [];
  if (mode.value === ECreateClassMode.MINIMAL) {
    if (navigateAfterCreationOverride.value == null) navigateAfterCreation.value = true;
    void handleFinalizeStepDone();
    return;
  }
  showNextStep();
};

const handleStudentsUpdate = (data: IStudentFormSelection): void => {
  const selectedCount = data.selectedStudents?.length ?? 0;
  studentsSelectionCount.value = data.students.length + selectedCount;
};

const handleNavigateToClass = async (schoolClass: Components.Schemas.SchoolClassDto): Promise<void> => {
  setCurrentClass(schoolClass);
  await navigateToAllStudents();
};

const confirmBumpUp = async (schoolClass: Components.Schemas.SchoolClassDto): Promise<boolean> => {
  if (schoolClass.archivedAt) return true;

  const archiveClassConfirm = await confirm({
    title: tContainer('bumpUpArchiveTitle'),
    description: tContainer('bumpUpArchiveHint'),
    confirmButtonLabel: tContainer('archive'),
    declineButtonLabel: tContainer('bumpUpWithoutArchiving'),
    theme: 'primary',
    size: 'lg',
  });

  if (!archiveClassConfirm) {
    return true;
  }

  archiveClassDialogRef.value?.show({ classesOrGroups: [schoolClass] });
  dialogRef.value?.hide();
  return false;
};

const handleFinalizeStepDone = async (): Promise<void> => {
  if (isLoading.value) return;
  await catchError(async () => {
    assertNotNullOrUndefined(generalData.value);
    isLoading.value = true;
    const isStudentOnboardingFinalized = !!newStudents.value.length || !!existingStudents.value.length;

    const dto: ICreateOrUpdateSchoolClass = {
      name: generalData.value.name,
      nickname: generalData.value.nickname ?? '',
      schoolLevel: generalData.value.schoolLevel,
      schoolYearId: generalData.value.schoolYearId,
      isStudentOnboardingFinalized,
      // a bumped group stays a group
      type: schoolClassToBumpUp.value?.type,
      ...(mode.value === ECreateClassMode.MINIMAL ? { isFullyOnboarded: true } : {}),
    };

    const bumpUpBaseId = schoolClassToBumpUp.value?.id;
    if (bumpUpBaseId != null) {
      dto.baseClassId = bumpUpBaseId;
    }

    const normalUsers = team.value.filter((u) => !u.isInvitation);
    const invitations = team.value.filter((u) => u.isInvitation);

    const schoolClass =
      savedSchoolClass.value ??
      (await createSchoolClassSetup({
        appearance: appearance.value,
        onCreated: (created) => {
          savedSchoolClass.value = created;
        },
        createSchoolClassDto: dto,
        schoolSubjectIdsToCopy: schoolSubjects.value.map((subject) => subject.id),
        accessRights: normalUsers.map((u) => ({
          email: u.user.email,
          canManageAccessRights: u.permissions.canManageAccessRights,
          canManageStudents: u.permissions.canManageStudents,
          canManagePerformanceAssessments: u.permissions.canManagePerformanceAssessments,
          canManageCompetences: u.permissions.canManageCompetences,
          subjectIds: u.schoolSubjects.map((s) => s.id),
        })),
        invitationAccessRights: invitations.map((u) => ({
          email: u.user.email,
          invitationId: u.user.id,
          canManageAccessRights: u.permissions.canManageAccessRights,
          canManageStudents: u.permissions.canManageStudents,
          canManagePerformanceAssessments: u.permissions.canManagePerformanceAssessments,
          canManageCompetences: u.permissions.canManageCompetences,
          subjectIds: u.schoolSubjects.map((s) => s.id),
        })),
        studentsToCreate: newStudents.value,
        existingStudentIds: existingStudents.value.map((student) => student.id),
      }));

    const creationMode = mode.value;
    const shouldNavigateAfterCreation = navigateAfterCreationOverride.value ?? navigateAfterCreation.value;
    await refresh();
    if (!newStudents.value.some((student) => student.avatarUploadFailed)) {
      customSuccessToast(t('classes.createSuccess', { name: getClassName(generalData.value) }));
    }
    didCreate.value = true;
    dialogRef.value?.hide();

    if (shouldNavigateAfterCreation) {
      await handleNavigateToClass(schoolClass);
    }

    classCreationStore.setClassCreated();
    emit('created', { schoolClass, mode: creationMode });
  }).finally(() => {
    isLoading.value = false;
  });
};

const handleSubmit = async (): Promise<void> => {
  if (isLoading.value) return;
  switch (currentStep.value) {
    case ECreateClassStep.SELECT_MODE:
      selectModeStepRef.value?.onSubmit();
      break;
    case ECreateClassStep.SELECT_CLASS:
      selectClassStepRef.value?.onSubmit();
      break;
    case ECreateClassStep.GENERAL_DATA:
      generalDataFormRef.value?.onSubmit();
      break;
    case ECreateClassStep.SCHOOL_SUBJECTS:
      schoolSubjectsFormRef.value?.onSubmit();
      break;
    case ECreateClassStep.NO_SCHOOL_SUBJECTS:
      break;
    case ECreateClassStep.TEAM:
      teamFormRef.value?.onSubmit();
      break;
    case ECreateClassStep.STUDENTS:
      studentsFormRef.value?.onSubmit();
      break;
    case ECreateClassStep.FINALIZE:
      await handleFinalizeStepDone();
      break;
  }
};

const canContinue = computed(() => {
  if (isLoading.value) return false;
  if (currentStep.value === ECreateClassStep.NO_SCHOOL_SUBJECTS) return false;
  if (currentStep.value === ECreateClassStep.STUDENTS && mode.value === ECreateClassMode.MINIMAL) {
    return formValid.value && studentsSelectionCount.value > 0;
  }
  return formValid.value;
});

/**
 * this has 3 flows:
 * 1. new class: creates a new class
 * 2. bump up class: bumps up an existing class (confirm if not archived)
 * 3. select class: selects an existing class to bump up
 */
const show = async (params: {
  mode: ECreateClassMode;
  navigateAfterCreation?: boolean;
  schoolClass?: Components.Schemas.SchoolClassDto;
}): Promise<void> => {
  isOpen.value = true;
  try {
    await refreshSchoolStudents();
  } catch (error) {
    isOpen.value = false;
    throw error;
  }
  mode.value = params.mode;
  navigateAfterCreationOverride.value = params.navigateAfterCreation;
  if (params.schoolClass && params.mode === ECreateClassMode.BUMP_UP_CLASS) {
    currentStep.value = ECreateClassStep.GENERAL_DATA;
    const confirmed = await handleBumpUpClassWithConfirm(params.schoolClass);
    if (!confirmed) {
      isOpen.value = false;
      return;
    }
  }
  if (params.mode === ECreateClassMode.SELECT_CLASS) {
    currentStep.value = ECreateClassStep.SELECT_MODE;
  }
  dialogRef.value?.show();
  await nextTick();
  captureSnapshot();
};

defineExpose({
  show,
});
</script>

<template>
  <DialogBase
    ref="dialogRef"
    :show-close-button="!isLoading"
    :disable-internal-hide-logic="isLoading"
    :title="isBumpUpFlow ? tContainer('bumpUp') : t('classes.create')"
    :fill-height="currentStep !== ECreateClassStep.SELECT_MODE"
    no-padding
    :confirm-config="discardChangesConfirmConfig"
    @hide="handleHide"
  >
    <BumpUpClassSelectModeStep
      v-if="currentStep === ECreateClassStep.SELECT_MODE"
      ref="selectModeStepRef"
      v-model="mode"
      @continue="handleSelectModeStepDone"
    />

    <BumpUpClassSelectClassStep
      v-if="currentStep === ECreateClassStep.SELECT_CLASS"
      ref="selectClassStepRef"
      :selected-school-class="schoolClassToBumpUp"
      @continue="handleSelectClassStepDone"
      @update:is-valid="formValid = $event"
    />

    <CreateClassGeneralDataForm
      v-if="currentStep === ECreateClassStep.GENERAL_DATA"
      ref="generalDataFormRef"
      v-model:appearance="appearance"
      :mode="mode"
      :school-class="generalData"
      @continue="handleGeneralDataStepDone"
      @update:is-valid="formValid = $event"
    />

    <CreateClassSchoolSubjectsForm
      v-if="currentStep === ECreateClassStep.SCHOOL_SUBJECTS"
      ref="schoolSubjectsFormRef"
      :school-level="generalData?.schoolLevel"
      :school-subjects="schoolSubjects"
      :available-school-subjects="availableSchoolSubjects"
      :available-school-subject-groups="allSchoolSubjectGroupTemplates"
      :single-select="isBumpingGroup"
      @update:available-school-subjects="availableSchoolSubjects = $event"
      @back="showPreviousStep"
      @continue="handleSchoolSubjectsStepDone"
      @update:is-valid="formValid = $event"
    />

    <CreateClassNoSchoolSubjectsStep
      v-if="currentStep === ECreateClassStep.NO_SCHOOL_SUBJECTS"
      :available-school-subjects="availableSchoolSubjects"
      @click:create-competences="goToSchoolCompetences"
    />

    <CreateClassTeamForm
      v-if="currentStep === ECreateClassStep.TEAM"
      ref="teamFormRef"
      :current-team="team"
      :available-school-subjects="schoolSubjects"
      :available-school-subject-groups="allSchoolSubjectGroupTemplates"
      allow-inline-adding-users
      @back="showPreviousStep"
      @continue="handleTeamStepDone"
      @update:is-valid="formValid = $event"
    />

    <CreateClassStudentsForm
      v-if="currentStep === ECreateClassStep.STUDENTS"
      ref="studentsFormRef"
      :disabled="isLoading"
      :already-added-students="newStudents"
      :already-selected-students="existingStudents"
      :school-class="generalData"
      :selectable-students="allStudentsAdmin"
      @back="showPreviousStep"
      @continue="handleStudentsStepDone"
      @update:students="handleStudentsUpdate"
      @update:is-valid="formValid = $event"
    />

    <CreateClassFinalize
      v-if="currentStep === ECreateClassStep.FINALIZE"
      :appearance="appearance"
      :general-data="generalData"
      :school-subjects="schoolSubjects"
      :team="team"
      :student-count="existingStudents.length + newStudents.length"
      @update:is-valid="formValid = $event"
    />

    <template #footer>
      <ClassFormFooter
        :footer-config="footerConfig"
        :form-valid="canContinue"
        @click:next="handleSubmit"
        @click:previous="showPreviousStep"
      >
        <div v-if="currentStep === ECreateClassStep.FINALIZE">
          <CustomCheckbox v-model="navigateAfterCreation" align-items="center" class="w-fit">
            {{ tContainer('switchToAfterCreate') }}
          </CustomCheckbox>
        </div>
      </ClassFormFooter>
    </template>
  </DialogBase>
  <ArchiveClassDialog ref="archiveClassDialogRef" />
</template>
